import { useState, useEffect } from 'react';
import api from '../components/api';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';

const useNotifications = (filters, activeTab) => {
  const [notifications, setNotifications] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showNotificationForm, setShowNotificationForm] = useState(false);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  
  // États de pagination
  const [notificationsPagination, setNotificationsPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 12
  });
  
  const [templatesPagination, setTemplatesPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 12
  });

  // Chargement des données
  useEffect(() => {
    fetchData();
  }, [filters, activeTab, notificationsPagination.currentPage, templatesPagination.currentPage]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'notifications') {
        await Promise.all([
          fetchNotifications(),
          fetchStatistics()
        ]);
      } else {
        await fetchTemplates();
      }
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          params.append(key, filters[key]);
        }
      });
      params.append('page', notificationsPagination.currentPage);
      params.append('page_size', notificationsPagination.itemsPerPage);

      const response = await api.get(`/api/utilisateur/admin/notifications/?${params}`);
      
      // Gérer la pagination
      if (response.data.results) {
        setNotifications(response.data.results);
        setNotificationsPagination(prev => ({
          ...prev,
          totalItems: response.data.count,
          totalPages: Math.ceil(response.data.count / prev.itemsPerPage)
        }));
      } else {
        setNotifications(response.data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des notifications:', error);
      toast.error('Erreur lors du chargement des notifications', {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await api.get('/api/utilisateur/admin/notifications/stats/');
      setStatistics(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const params = new URLSearchParams();
      params.append('page', templatesPagination.currentPage);
      params.append('page_size', templatesPagination.itemsPerPage);
      
      const response = await api.get(`/api/utilisateur/admin/notifications-templates/?${params}`);
      
      // Gérer la pagination
      if (response.data.results) {
        setTemplates(response.data.results);
        setTemplatesPagination(prev => ({
          ...prev,
          totalItems: response.data.count,
          totalPages: Math.ceil(response.data.count / prev.itemsPerPage)
        }));
      } else {
        setTemplates(response.data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des modèles:', error);
      toast.error('Erreur lors du chargement des modèles', {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  // Handlers Notifications
  const handleCreateNotification = () => {
    setSelectedNotification(null);
    setShowNotificationForm(true);
  };

  const handleEditNotification = (notification) => {
    setSelectedNotification(notification);
    setShowNotificationForm(true);
    setShowDetail(false);
  };

  const handleViewDetail = (notification) => {
    setSelectedNotification(notification);
    setShowDetail(true);
  };

  const handleSaveNotification = async (data) => {
    try {
      if (selectedNotification) {
        await api.put(`/api/utilisateur/admin/notifications/${selectedNotification.id}/`, data);
        toast.success('Notification mise à jour avec succès', {
          position: "top-right",
          autoClose: 3000,
        });
      } else {
        await api.post('/api/utilisateur/admin/notifications/', data);
        toast.success('Notification créée avec succès', {
          position: "top-right",
          autoClose: 3000,
        });
      }
      await fetchData();
      setShowNotificationForm(false);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la sauvegarde', {
        position: "top-right",
        autoClose: 4000,
      });
      throw error;
    }
  };

  const handleSendNotification = async (notificationId) => {
    const result = await Swal.fire({
      title: 'Confirmer l\'envoi',
      text: 'Êtes-vous sûr de vouloir envoyer cette notification ?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#FF6B00',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Oui, envoyer',
      cancelButtonText: 'Annuler'
    });

    if (result.isConfirmed) {
      try {
        const response = await api.post(`/api/utilisateur/admin/notifications/${notificationId}/envoyer/`);
        await fetchData();
        toast.success(response.data.message || 'Notification envoyée avec succès', {
          position: "top-right",
          autoClose: 3000,
        });
      } catch (error) {
        console.error('Erreur lors de l\'envoi:', error);
        toast.error('Erreur lors de l\'envoi de la notification', {
          position: "top-right",
          autoClose: 4000,
        });
      }
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    const result = await Swal.fire({
      title: 'Confirmer la suppression',
      text: 'Êtes-vous sûr de vouloir supprimer cette notification ? Cette action est irréversible.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#FF6B00',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/api/utilisateur/admin/notifications/${notificationId}/`);
        await fetchData();
        setShowDetail(false);
        toast.success('Notification supprimée avec succès', {
          position: "top-right",
          autoClose: 3000,
        });
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        toast.error('Erreur lors de la suppression', {
          position: "top-right",
          autoClose: 4000,
        });
      }
    }
  };

  // Handlers Templates
  const handleCreateTemplate = () => {
    setSelectedTemplate(null);
    setShowTemplateForm(true);
  };

  const handleEditTemplate = (template) => {
    setSelectedTemplate(template);
    setShowTemplateForm(true);
  };

  const handleSaveTemplate = async (data) => {
    try {
      if (selectedTemplate) {
        await api.put(`/api/utilisateur/admin/notifications-templates/${selectedTemplate.id}/`, data);
        toast.success('Modèle mis à jour avec succès', {
          position: "top-right",
          autoClose: 3000,
        });
      } else {
        await api.post('/api/utilisateur/admin/notifications-templates/', data);
        toast.success('Modèle créé avec succès', {
          position: "top-right",
          autoClose: 3000,
        });
      }
      await fetchTemplates();
      setShowTemplateForm(false);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast.error('Erreur lors de la sauvegarde du modèle', {
        position: "top-right",
        autoClose: 4000,
      });
      throw error;
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    const result = await Swal.fire({
      title: 'Confirmer la suppression',
      text: 'Êtes-vous sûr de vouloir supprimer ce modèle ?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#FF6B00',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/api/utilisateur/admin/notifications-templates/${templateId}/`);
        await fetchTemplates();
        toast.success('Modèle supprimé avec succès', {
          position: "top-right",
          autoClose: 3000,
        });
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        toast.error('Erreur lors de la suppression', {
          position: "top-right",
          autoClose: 4000,
        });
      }
    }
  };

  const handleUseTemplate = (template) => {
    setSelectedNotification({
      titre: template.nom,
      message: template.message,
      type_notification: template.type_notification,
      priorite: template.priorite
    });
    setShowNotificationForm(true);
  };

  // Handlers de pagination
  const handleNotificationPageChange = (page) => {
    setNotificationsPagination(prev => ({
      ...prev,
      currentPage: page
    }));
  };

  const handleTemplatePageChange = (page) => {
    setTemplatesPagination(prev => ({
      ...prev,
      currentPage: page
    }));
  };

  return {
    // Data
    notifications,
    templates,
    statistics,
    loading,
    selectedNotification,
    selectedTemplate,
    showNotificationForm,
    showTemplateForm,
    showDetail,
    
    // Pagination
    notificationsPagination,
    templatesPagination,
    handleNotificationPageChange,
    handleTemplatePageChange,
    
    // Setters
    setShowNotificationForm,
    setShowTemplateForm,
    setShowDetail,
    
    // Handlers Notifications
    handleCreateNotification,
    handleEditNotification,
    handleViewDetail,
    handleSaveNotification,
    handleSendNotification,
    handleDeleteNotification,
    
    // Handlers Templates
    handleCreateTemplate,
    handleEditTemplate,
    handleSaveTemplate,
    handleDeleteTemplate,
    handleUseTemplate
  };
};

export default useNotifications;
